/**
 * Network Registry APIs (NETWORK-REGISTRY.md) — the catalog CONSUMER client.
 *
 * The app-browse page (/apps) + deploy wizard read/deploy published packages via
 * these endpoints (shared-services registry, served through the workload REST API):
 *
 *   GET  /registry/packages?domain=&q=                        → { packages, count, domain }
 *   GET  /registry/packages/{registryNetworkId}?version=      → { package }
 *   GET  /registry/packages/{registryNetworkId}/versions      → { registryNetworkId, versions, count }
 *   POST /registry/packages/{registryNetworkId}/deploy        → { networkId, pool, releaseVersion, ... }
 *
 * Mirrors the ``authedFetch`` convention in ``poolSchedulesApi.js`` (bearer token,
 * VITE_API_BASE_URL, JSON error with ``err.status``/``err.data``). Publishing +
 * release/unrelease curation live in ``networkRestoreApi.ts`` (author side); this
 * module is the browse/deploy (consumer) side.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function encodePath(s) {
  return encodeURIComponent(String(s || '').trim());
}

async function authedFetch(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || data.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Browse released packages. Optional ``domain`` facet + free-text ``q`` search.
 * @param {{ domain?: string, q?: string }} [opts]
 * @returns {Promise<{ packages: object[], count: number, domain: string }>}
 */
export async function listRegistryPackages(opts = {}) {
  const params = new URLSearchParams();
  if (opts.domain) params.set('domain', String(opts.domain));
  if (opts.q) params.set('q', String(opts.q));
  const qs = params.toString();
  return authedFetch(`/registry/packages${qs ? `?${qs}` : ''}`);
}

// Provenance filter keys the drafts feed accepts (each defaults to all/absent).
const DRAFT_SOURCE_FILTERS = ['awsAccountId', 'orgId', 'tenantId', 'userId', 'sourceNetworkId'];

/**
 * Curation feed (ADMIN-only): every DRAFT package version awaiting approval.
 * Backend enforces workspace OWNER/ADMIN + dev-env. All filters default to ALL
 * (omit → not applied); supplied ones AND together. Each returned package includes
 * its ``source`` provenance map (curation view only).
 * @param {{ domain?: string, q?: string, awsAccountId?: string, orgId?: string,
 *           tenantId?: string, userId?: string, sourceNetworkId?: string }} [opts]
 * @returns {Promise<{ packages: object[], count: number, domain: string, filters: object }>}
 */
export async function listRegistryDrafts(opts = {}) {
  const params = new URLSearchParams();
  if (opts.domain) params.set('domain', String(opts.domain));
  if (opts.q) params.set('q', String(opts.q));
  for (const k of DRAFT_SOURCE_FILTERS) {
    if (opts[k]) params.set(k, String(opts[k]));
  }
  const qs = params.toString();
  return authedFetch(`/registry/drafts${qs ? `?${qs}` : ''}`);
}

/**
 * Whether the caller may curate (show the Review tab). True iff the backend runs
 * in the curation environment (dev) AND the caller is a curation role — so the FE
 * carries no environment literal; dev-ness is decided server-side.
 * @returns {Promise<{ canCurate: boolean }>}
 */
export async function getRegistryCurationCapability() {
  return authedFetch('/registry/curation-capability');
}

/**
 * Approve a DRAFT package → RELEASED (curator action; ADMIN-only). Flips the row
 * status; the zip never moves. Once approved it appears in the released catalog.
 * @param {string} registryNetworkId
 * @param {number} version
 * @returns {Promise<{ registryNetworkId: string, version: number, status: string, package: object }>}
 */
export async function approveRegistryPackage(registryNetworkId, version) {
  return authedFetch(
    `/registry/${encodePath(registryNetworkId)}/versions/${encodePath(version)}/release`,
    { method: 'POST', body: '{}' },
  );
}

/**
 * Archive a DRAFT package → ARCHIVED (curator; ADMIN-only). Pulls it out of the
 * Review queue into the Archived tab; row + zip stay. Restorable via review.
 */
export async function archiveRegistryPackage(registryNetworkId, version) {
  return authedFetch(
    `/registry/${encodePath(registryNetworkId)}/versions/${encodePath(version)}/archive`,
    { method: 'POST', body: '{}' },
  );
}

/**
 * Restore an ARCHIVED package back into Review → DRAFT (curator; ADMIN-only).
 */
export async function reviewRegistryPackage(registryNetworkId, version) {
  return authedFetch(
    `/registry/${encodePath(registryNetworkId)}/versions/${encodePath(version)}/review`,
    { method: 'POST', body: '{}' },
  );
}

/**
 * PERMANENTLY delete a package version — the DDB row AND the S3 zip (curator;
 * ADMIN-only). Irreversible.
 */
export async function deleteRegistryPackage(registryNetworkId, version) {
  return authedFetch(
    `/registry/${encodePath(registryNetworkId)}/versions/${encodePath(version)}`,
    { method: 'DELETE' },
  );
}

/**
 * Archived feed (ADMIN-only): every ARCHIVED package version (the Archived tab).
 * Same filters/shape as ``listRegistryDrafts``.
 */
export async function listRegistryArchived(opts = {}) {
  const params = new URLSearchParams();
  if (opts.domain) params.set('domain', String(opts.domain));
  if (opts.q) params.set('q', String(opts.q));
  for (const k of DRAFT_SOURCE_FILTERS) {
    if (opts[k]) params.set(k, String(opts[k]));
  }
  const qs = params.toString();
  return authedFetch(`/registry/archived${qs ? `?${qs}` : ''}`);
}

/**
 * One package's detail. Without ``version`` → the latest RELEASED version.
 * @param {string} registryNetworkId
 * @param {number} [version]
 * @returns {Promise<{ package: object }>}
 */
export async function getRegistryPackage(registryNetworkId, version) {
  const v = Number.isFinite(version) ? `?version=${encodeURIComponent(String(version))}` : '';
  return authedFetch(`/registry/packages/${encodePath(registryNetworkId)}${v}`);
}

/**
 * Deep read-only detail of a package (reads the zip server-side): ASL Comment,
 * rich ASL (for the diagram), agent role/prompt cards, and skill SKILL.md bodies.
 * @param {string} registryNetworkId
 * @param {number} [version]
 * @returns {Promise<{ package: object, comment: string, asl: object,
 *   agents: {logicalId,role,systemPrompt,modelId,type,tools}[],
 *   skills: {skillId,skillMd}[], context: object }>}
 */
export async function getRegistryPackageDetail(registryNetworkId, version) {
  const v = Number.isFinite(version) ? `?version=${encodeURIComponent(String(version))}` : '';
  return authedFetch(`/registry/packages/${encodePath(registryNetworkId)}/detail${v}`);
}

/**
 * All versions of a registry network, newest first.
 * @returns {Promise<{ registryNetworkId: string, versions: object[], count: number }>}
 */
export async function listRegistryPackageVersions(registryNetworkId) {
  return authedFetch(`/registry/packages/${encodePath(registryNetworkId)}/versions`);
}

/**
 * Deploy a package into the caller's tenant: import → seed → (optionally) release
 * one room. Pass ``createRoom:false`` for the single-pane UX (§14) — lands a bare
 * ACTIVE network (no room); the caller configures connectors + creates rooms
 * afterward from the network tile. Default (``createRoom`` omitted/true) keeps the
 * legacy one-shot behavior (import + one room).
 * @param {string} registryNetworkId
 * @param {{ version?: number, name?: string, createRoom?: boolean, poolName?: string,
 *           owner?: string, supervisors?: string[], trigger?: string,
 *           bootstrapWorkspace?: boolean, codeConnectorSecretId?: string,
 *           poolConnectors?: object, agentConnectors?: object }} [body]
 * @returns {Promise<{ networkId: string, roomCreated: boolean, poolId: string,
 *           pool: object|null, releaseVersion: number, sourceRegistryId: string,
 *           sourceVersion: number, seeded: object }>}
 */
export async function deployRegistryPackage(registryNetworkId, body = {}) {
  return authedFetch(`/registry/packages/${encodePath(registryNetworkId)}/deploy`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

/** PLUGINS.md §3 experts — create the EXPERT NETWORK a plugin ships (its evolved package: agents, rules, procedures,
 *  trained answers), wired to the plugin, in THIS workspace — the registry's own deploy, from the plugin's folder.
 *  Rooms are created afterwards (as for any network). → ``{networkId, …}``. */
export async function createNetworkFromPlugin(pluginId, expertId, { name = '' } = {}) {
  return deployRegistryPackage(`plugin:${pluginId}:${expertId}`, { createRoom: false, ...(name ? { name } : {}) });
}

/**
 * Config-completeness for a registry-deployed network (§14): reconciles the source
 * package's toolsUsed against live bindings to derive green/yellow. Powers the tile
 * color + the "resume where you left off" connector/MCP checklist. Read-only.
 * @param {string} networkId
 * @returns {Promise<{ networkId: string, registryDeployed: boolean,
 *           state: 'green'|'yellow'|'unknown',
 *           network: { required: number, configured: number, missing: object[] },
 *           rooms: { poolId, poolName, required, configured, missing, state }[] }>}
 */
export async function getRegistryConfigStatus(networkId) {
  return authedFetch(`/networks/${encodePath(networkId)}/registry-config-status`);
}

/**
 * Upgrade a registry-deployed network to a newer registry version — imports the
 * newer package onto the EXISTING network as a new release (agents/graph/skills
 * replaced). Server resolves the latest RELEASED version when ``targetVersion``
 * is omitted. Rooms are NOT re-pinned here (that's the separate per-pool flow).
 * @param {string} networkId  the deployed network to upgrade
 * @param {number} [targetVersion]  registry version to move to (default: latest)
 * @returns {Promise<{ networkId, releaseVersion, sourceRegistryVersion, ... }>}
 */
export async function upgradeNetworkFromRegistry(networkId, targetVersion) {
  const body = Number.isFinite(targetVersion) ? { targetVersion } : {};
  return authedFetch(`/networks/${encodePath(networkId)}/upgrade-from-registry`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ── CONTRIBUTIONS (EXPERT-ROOMS §16) ──────────────────────────────────────────────────────────────────────────────
/** The contributor's view for one expert: upstream, opt-in, what can be offered (scrubbed), what was sent + status. */
export async function getExpertContributions(expertNetworkId) {
  return authedFetch(`/registry/contributions?expertNetworkId=${encodeURIComponent(expertNetworkId)}`);
}
/** ``{action: 'optIn', on}`` (workspace owner) | ``{action: 'send', items, note, consent: true}`` (owner / admin). */
export async function postExpertContribution(expertNetworkId, body) {
  return authedFetch('/registry/contributions', { method: 'POST', body: JSON.stringify({ expertNetworkId, ...body }) });
}
/** Curators: the contributions queue (pending | accepted | declined). */
export async function listContributions(queue = 'pending') {
  return authedFetch(`/registry/contributions?queue=${encodeURIComponent(queue)}`);
}
/** Curators: accept / decline one, with a note the contributor sees. */
export async function decideContribution(contributionId, registryNetworkId, decision, note = '') {
  return authedFetch(`/registry/contributions/${encodeURIComponent(contributionId)}/decision`, {
    method: 'POST', body: JSON.stringify({ registryNetworkId, decision, note }),
  });
}
